import { extractAndInjectScripts, formatNostoErrorCode } from '@utilities/theme';
import { EVENTS, subscribe } from '@utilities/events';

// Nosto utilities are lazy-loaded so recommendation sections only download them
// when a Nosto placement is actually configured. #fetchAndRenderNostoRecommendations
// awaits this before use.
let nostoModulePromise = null;
function loadNostoUtilities() {
  if (!nostoModulePromise) {
    nostoModulePromise = import('@utilities/nosto');
  }
  return nostoModulePromise;
}

if(!customElements.get('product-recommendations')) {
  /**
   * ProductRecommendations custom element that represents a product recommendations section on the site
   * Handles fetching and rendering product recommendations
   * @class
   * @extends {HTMLElement}
   */
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.productId = parseInt(this.getAttribute('data-product-id'), 10);
      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 10;
      this.sectionId = this.getAttribute('data-section-id');
      this.intent = this.getAttribute('data-intent') || 'related';
      this.nostoPlacementId = this.getAttribute('data-nosto-placement-id') || '';
      this.body = this.querySelector('.product-recommendations__body');
      this.boundHandleVariantChange = this.#handleVariantChange.bind(this);
      this.boundHandleVendorChange = this.#handleVendorChange.bind(this);
      this.abortController = null;
      this.activeRequestId = 0;
    }

    #allowDebug() {
      return this.classList.contains('debug');
    }

    /**
     * @description Subscribe to events and fetch initial recommendations when the element is connected
     */
    connectedCallback() {
      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Debug mode enabled', this);
      }
      this.unsubscribers = [
        subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, this.boundHandleVariantChange),
      ];
      document.addEventListener('vendor:changed', this.boundHandleVendorChange);
      this.#fetchRecommendations();
    }

    /**
     * @description Clean up subscriptions when the element is disconnected
     */
    disconnectedCallback() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
      document.removeEventListener('vendor:changed', this.boundHandleVendorChange);
      this.abortController?.abort();
      this.abortController = null;
    }

    /**
     * @private
     * @description Handle variant change event
     * @param {Object} data - Published event data
     */
    #handleVariantChange(data) {
      const productId = parseInt(data?.variant?.product_id, 10);
      if(this.#allowDebug()) {
        console.log('ProductRecommendations: Variant change event', productId, this.productId);
      }
      if (!Number.isFinite(productId)) {
        if (this.#allowDebug()) {
          console.log('ProductRecommendations: Ignoring variant change without valid product id', data);
        }
        return;
      }
      // if the product ID is the same as the current product ID, do nothing
      // otherwise we are most likely dealing with a child product change from a swatch change.
      if (productId === this.productId) return;
      this.#refresh({ productId: productId });
    }

    #handleVendorChange(event) {
      const vendor = event?.detail?.vendor || this.#getCurrentVendor();

      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Vendor changed', vendor);
      }

      this.#refresh();
    }

    #getCurrentVendor() {
      return window.theme?.data?.source || 'shopify';
    }

    #buildShopifyRecommendationsUrl() {
      return `${window.theme.routes.product_recommendations_url}?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}&section_id=${this.sectionId}`;
    }

    #isLatestRequest(requestId) {
      return requestId === this.activeRequestId;
    }

    /**
     * @private
     * @description Fetch product recommendations using section rendering API
     */
    async #fetchRecommendations() {
      if (!Number.isFinite(this.productId)) {
        console.warn('ProductRecommendations: No product ID provided');
        return;
      }

      const requestId = ++this.activeRequestId;
      const vendor = this.#getCurrentVendor();

      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Fetching recommendations', {
          productId: this.productId,
          limit: this.limit,
          intent: this.intent,
          sectionId: this.sectionId,
          vendor,
        });
      }

      try {
        if (vendor === 'nosto') {
          const rendered = await this.#fetchAndRenderNostoRecommendations(requestId);
          if (rendered) {
            return;
          }

          if (this.#allowDebug()) {
            console.warn('ProductRecommendations: Nosto returned no renderable results, falling back to Shopify');
          }
        }

        await this.#fetchAndRenderShopifyRecommendations(requestId);
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error('ProductRecommendations: Failed to fetch recommendations', error);

        if (vendor === 'nosto') {
          // GC-2374: a Nosto request failure here (anything other than "no results", which is
          // handled by the empty-check in #fetchAndRenderNostoRecommendations, not a throw)
          // disables Nosto for the page and emits datajet:nosto_failure. We still render this
          // component's own Shopify fallback immediately below so recommendations aren't blank.
          if (window.theme?.utilities?.disableNostoForPage) {
            window.theme.utilities.disableNostoForPage(formatNostoErrorCode(error) || error.message, error.code);
          }

          try {
            if (this.#allowDebug()) {
              console.warn('ProductRecommendations: Nosto request failed, attempting Shopify fallback');
            }
            await this.#fetchAndRenderShopifyRecommendations(requestId);
          } catch (fallbackError) {
            console.error('ProductRecommendations: Shopify fallback failed', fallbackError);
          }
        }
      }
    }

    async #fetchAndRenderShopifyRecommendations(requestId) {
      const html = await this.#fetchShopifyRecommendationsHtml(requestId);
      if (!this.#isLatestRequest(requestId)) return false;
      return this.#renderRecommendationsFromHtml(html, requestId);
    }

    async #fetchShopifyRecommendationsHtml(requestId) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const fetchUrl = this.#buildShopifyRecommendationsUrl();
      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Fetching Shopify recommendations', fetchUrl);
      }

      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      if (!this.#isLatestRequest(requestId)) {
        return '';
      }
      return text;
    }

    async #fetchAndRenderNostoRecommendations(requestId) {
      if (!this.nostoPlacementId) {
        if (this.#allowDebug()) {
          console.warn('ProductRecommendations: Missing data-nosto-placement-id');
        }
        return false;
      }

      const { nostoCreateRecommendationRequest, nostoAttributeProductClicksInCampaign } =
        await loadNostoUtilities();

      const nostoData = await nostoCreateRecommendationRequest({
        placementId: this.nostoPlacementId,
        productId: this.productId,
        intent: this.intent,
        limit: this.limit,
        debug: this.#allowDebug(),
      });

      if (!this.#isLatestRequest(requestId)) {
        return false;
      }

      if (!Array.isArray(nostoData?.products) || nostoData.products.length === 0) {
        return false;
      }

      const shellHtml = await this.#fetchShopifyRecommendationsHtml(requestId);
      if (!this.#isLatestRequest(requestId)) {
        return false;
      }

      return this.#renderNostoRecommendations(shellHtml, nostoData, requestId, nostoAttributeProductClicksInCampaign);
    }

    #renderNostoRecommendations(shellHtml, nostoData, requestId, attributeProductClicksInCampaign) {
      if (!shellHtml || !this.body) {
        return false;
      }

      const parsedHTML = new DOMParser().parseFromString(shellHtml, 'text/html');
      const shellRecommendationsBody = parsedHTML.querySelector('.product-recommendations__body');

      let workingBody = null;
      if (shellRecommendationsBody?.querySelector('product-slider')) {
        workingBody = shellRecommendationsBody.cloneNode(true);
      } else if (this.body.querySelector('product-slider')) {
        workingBody = this.body.cloneNode(true);
      }

      if (!workingBody) {
        if (this.#allowDebug()) {
          console.warn('ProductRecommendations: Unable to locate a product-slider shell for Nosto render');
        }
        return false;
      }

      const configScript = workingBody.querySelector('product-slider script[component-config]');
      if (!configScript) {
        if (this.#allowDebug()) {
          console.warn('ProductRecommendations: Missing product-slider component config script');
        }
        return false;
      }

      let sliderConfig = {};
      try {
        sliderConfig = JSON.parse(configScript.textContent || '{}');
      } catch (error) {
        console.error('ProductRecommendations: Invalid product-slider config JSON', error);
        return false;
      }

      sliderConfig.vendor = 'nosto';
      sliderConfig.totalSlides = this.limit;
      sliderConfig.givenProducts = nostoData.products.map((product) => ({
        id: product.id,
        url: product.url,
        default_variant_id: product.defaultVariantId,
      }));

      configScript.textContent = JSON.stringify(sliderConfig, null, 2);

      if (!this.#isLatestRequest(requestId)) {
        return false;
      }

      this.body.replaceChildren(...workingBody.childNodes);
      extractAndInjectScripts(this.body, null, { debug: this.#allowDebug() });
      this.body.classList.add('product-recommendations__body--loaded');

      const campaignContainer = this.body.querySelector('product-slider') || this.body;
      attributeProductClicksInCampaign(campaignContainer, nostoData.campaign, {
        debug: this.#allowDebug(),
      }).catch((error) => {
        if (this.#allowDebug()) {
          console.error('ProductRecommendations: Failed to attach campaign attribution', error);
        }
      });

      return true;
    }

    /**
     * @private
     * @description Render recommendations from HTML response
     * @param {string} html - The HTML response
     * @param {number} requestId - Request token for stale response protection
     * @returns {boolean} Whether recommendations were rendered
     */
    #renderRecommendationsFromHtml(html, requestId) {
      if (!html || !this.body) return false;
      if (!this.#isLatestRequest(requestId)) return false;

      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const recommendations = parsedHTML.querySelector('.product-recommendations__body');
      if (!recommendations || !recommendations.innerHTML.trim().length) return false;

      this.body.replaceChildren(...recommendations.childNodes);
      extractAndInjectScripts(this.body, null, { debug: this.#allowDebug() });
      this.body.classList.add('product-recommendations__body--loaded');
      return true;
    }

    /**
     * @private
     * @description Refresh recommendations with new parameters
     * @param {Object} options - New options
     * @param {string} [options.intent] - Recommendation intent
     * @param {number} [options.limit] - Number of recommendations
     */
    #refresh(options = {}) {
      if (this.#allowDebug()) {
        console.log('ProductRecommendations: Refreshing recommendations', options);
      }
      if (Number.isFinite(options.productId)) this.productId = options.productId;
      if (options.nostoPlacementId) this.nostoPlacementId = options.nostoPlacementId;
      if (options.intent) this.intent = options.intent;
      if (options.limit) this.limit = options.limit;

      this.#fetchRecommendations();
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
}
